import { Head, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Plus, Download, Target, ChevronDown, ChevronUp, ChevronRight, Check } from 'lucide-react'
import { useState, useMemo } from 'react'
import { type DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { PeriodNavigator } from '@/components/PeriodNavigator'
import { QuickAddModal } from '@/components/time-entries/QuickAddModal'
import { EditModal } from '@/components/time-entries/EditModal'
import { ActivityTypeFilter } from '@/components/ActivityTypeFilter'
import { GoalModal } from '@/components/GoalModal'
import { useKeyboardShortcuts } from '@/lib/useKeyboardShortcuts'
import { formatDuration } from '@/lib/utils'
import type { TimeEntry, ActivitySubtype, ActivityType, ActivityTypeStats, Goal, GoalPeriod } from '@/types'

type PeriodView = 'day' | 'week' | 'month' | 'year' | 'custom'

interface Props {
  // Calendar data
  time_entries: TimeEntry[]
  activity_subtypes: ActivitySubtype[]
  activity_types: ActivityType[]
  activity_type_filter: number[]
  filters: {
    start_date: string | null
    end_date: string | null
  }
  calendar_view: PeriodView
  calendar_date: string
  // Statistics data
  entries_by_activity: ActivityTypeStats[]
  goals: Goal[]
}

export default function TimeTracker({
  time_entries,
  activity_subtypes,
  activity_types,
  activity_type_filter,
  filters,
  calendar_view,
  calendar_date,
  entries_by_activity,
  goals,
}: Props) {
  const { t } = useTranslation()
  const [currentDate, setCurrentDate] = useState(() => new Date(calendar_date))
  const [view, setView] = useState<PeriodView>(calendar_view as PeriodView)
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedActivityTypes, setSelectedActivityTypes] = useState<number[]>(activity_type_filter)
  const [customRange, setCustomRange] = useState<DateRange | undefined>(() => {
    if (filters.start_date && filters.end_date) {
      return {
        from: new Date(filters.start_date),
        to: new Date(filters.end_date),
      }
    }
    return undefined
  })
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [goalModalOpen, setGoalModalOpen] = useState(false)
  const [goalModalActivityType, setGoalModalActivityType] = useState<ActivityType | null>(null)
  const [goalModalActivitySubtype, setGoalModalActivitySubtype] = useState<ActivitySubtype | null>(null)
  const [statsExpanded, setStatsExpanded] = useState(false)
  const [expandedActivityTypes, setExpandedActivityTypes] = useState<Set<number>>(new Set())

  // Map goals by goal_key for quick lookup (supports both type and subtype goals)
  const goalsByKey = useMemo(() => goals.reduce((acc, goal) => {
    acc[goal.goal_key] = goal
    return acc
  }, {} as Record<string, Goal>), [goals])

  // Build merged list: activities with goals (including 0% ones) + activities without goals
  const { activitiesWithGoals, activitiesWithoutGoals, activityTypesWithoutGoals, subtypeIdsWithGoals, totalTargetMinutes, totalActualMinutes } = useMemo(() => {
    // Get activity type IDs that have type-level goals
    const typeIdsWithGoals = new Set(goals.filter(g => g.activity_type_id !== null).map(g => g.activity_type_id!))
    // Get subtype IDs that have subtype-level goals
    const subtypeIdsWithGoalsSet = new Set(goals.filter(g => g.activity_subtype_id !== null).map(g => g.activity_subtype_id!))

    // Activities that have goals set (respect filter)
    // For type goals: filter by activity_type_id
    // For subtype goals: filter by parent activity type
    const withGoals = goals
      .filter(goal => {
        if (selectedActivityTypes.length === 0) return true
        if (goal.activity_type_id !== null) {
          return selectedActivityTypes.includes(goal.activity_type_id)
        }
        // For subtype goals, check if parent type is in filter
        const subtype = activity_subtypes.find(s => s.id === goal.activity_subtype_id)
        return subtype ? selectedActivityTypes.includes(subtype.activity_type.id) : false
      })
      .map(goal => {
        if (goal.activity_type_id !== null) {
          // Type-level goal
          const activity = entries_by_activity.find(a => a.id === goal.activity_type_id)
          const activityType = activity_types.find(t => t.id === goal.activity_type_id)
          return {
            id: goal.activity_type_id,
            goal_key: goal.goal_key,
            name: activity?.name || activityType?.name || 'Unknown',
            color: activity?.color || activityType?.color || '#888888',
            total_minutes: activity?.total_minutes || 0,
            goal,
            isSubtype: false,
          }
        } else {
          // Subtype-level goal
          const subtype = activity_subtypes.find(s => s.id === goal.activity_subtype_id)
          // Find subtype stats from parent activity's subtypes
          const parentActivity = entries_by_activity.find(a => a.id === subtype?.activity_type.id)
          const subtypeStats = parentActivity?.subtypes.find(s => s.id === goal.activity_subtype_id)
          return {
            id: goal.activity_subtype_id!,
            goal_key: goal.goal_key,
            name: subtype?.full_name || 'Unknown',
            color: subtype?.color || '#888888',
            total_minutes: subtypeStats?.total_minutes || 0,
            goal,
            isSubtype: true,
          }
        }
      })

    // Activities with time logged but no goal (for the bar)
    const withoutGoals = entries_by_activity.filter(
      a => !typeIdsWithGoals.has(a.id)
    )

    // All activity types without goals (for the expanded list, includes those with 0 time)
    // Respect the activity type filter - only show filtered types (or all if no filter)
    const typesWithoutGoals = activity_types.filter(
      t => !typeIdsWithGoals.has(t.id) &&
           (selectedActivityTypes.length === 0 || selectedActivityTypes.includes(t.id))
    ).map(t => {
      const activity = entries_by_activity.find(a => a.id === t.id)
      return {
        id: t.id,
        name: t.name,
        color: t.color,
        icon: t.icon,
        total_minutes: activity?.total_minutes || 0,
      }
    })

    const totalTarget = withGoals.reduce((sum, a) => sum + a.goal.target_minutes, 0)
    const totalActual = [...withGoals, ...withoutGoals].reduce((sum, a) => sum + a.total_minutes, 0)

    return {
      activitiesWithGoals: withGoals,
      activitiesWithoutGoals: withoutGoals,
      activityTypesWithoutGoals: typesWithoutGoals,
      subtypeIdsWithGoals: subtypeIdsWithGoalsSet,
      totalTargetMinutes: totalTarget,
      totalActualMinutes: totalActual,
    }
  }, [goals, entries_by_activity, activity_types, activity_subtypes, selectedActivityTypes])

  // Get the goal period based on current view
  const getGoalPeriod = (): GoalPeriod => {
    switch (view) {
      case 'day': return 'daily'
      case 'week': return 'weekly'
      case 'month': return 'monthly'
      case 'year': return 'monthly' // No yearly goals
      default: return 'weekly'
    }
  }

  // Keyboard shortcut for new entry
  const shortcuts = useMemo(
    () => [{ key: 'n', handler: () => setQuickAddOpen(true) }],
    []
  )

  useKeyboardShortcuts(shortcuts)

  const handleEntryClick = (entry: TimeEntry) => {
    setEditingEntry(entry)
    setEditModalOpen(true)
  }

  const openGoalModal = (activityType: ActivityType | null, activitySubtype: ActivitySubtype | null = null) => {
    setGoalModalActivityType(activityType)
    setGoalModalActivitySubtype(activitySubtype)
    setGoalModalOpen(true)
  }

  const toggleActivityTypeExpanded = (id: number) => {
    setExpandedActivityTypes(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  // Group entries by date
  const entriesByDate = time_entries.reduce((acc, entry) => {
    if (!acc[entry.date]) {
      acc[entry.date] = []
    }
    acc[entry.date].push(entry)
    return acc
  }, {} as Record<string, TimeEntry[]>)

  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate)
    const offset = direction === 'prev' ? -1 : 1

    switch (view) {
      case 'day':
        newDate.setDate(newDate.getDate() + offset)
        break
      case 'week':
        newDate.setDate(newDate.getDate() + offset * 7)
        break
      case 'month':
        newDate.setMonth(newDate.getMonth() + offset)
        break
      case 'year':
        newDate.setFullYear(newDate.getFullYear() + offset)
        break
    }

    setCurrentDate(newDate)
    fetchData(newDate, view, undefined, selectedActivityTypes)
  }

  const goToToday = () => {
    const today = new Date()
    setCurrentDate(today)
    fetchData(today, view, undefined, selectedActivityTypes)
  }

  const fetchData = (date: Date, currentView: PeriodView, range?: DateRange, activityTypeIds: number[] = []) => {
    let start: Date
    let end: Date

    switch (currentView) {
      case 'day':
        start = new Date(date)
        end = new Date(date)
        break
      case 'week': {
        const dayOfWeek = date.getDay()
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        start = new Date(date)
        start.setDate(date.getDate() + mondayOffset)
        end = new Date(start)
        end.setDate(start.getDate() + 6)
        break
      }
      case 'month':
        start = new Date(date.getFullYear(), date.getMonth(), 1)
        end = new Date(date.getFullYear(), date.getMonth() + 1, 0)
        break
      case 'year':
        start = new Date(date.getFullYear(), 0, 1)
        end = new Date(date.getFullYear(), 11, 31)
        break
      case 'custom':
        if (range?.from && range?.to) {
          start = range.from
          end = range.to
        } else {
          // Default to current week if no range selected
          const dayOfWeek = date.getDay()
          const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
          start = new Date(date)
          start.setDate(date.getDate() + mondayOffset)
          end = new Date(start)
          end.setDate(start.getDate() + 6)
        }
        break
    }

    // Map view to statistics period
    const period = currentView === 'day' ? 'week' : currentView

    router.post(
      '/',
      {
        start_date: start.toISOString().split('T')[0],
        end_date: end.toISOString().split('T')[0],
        view_mode: currentView,
        period,
        date: date.toISOString().split('T')[0],
        activity_type_ids: activityTypeIds,
      },
      {
        only: ['time_entries', 'filters', 'calendar_view', 'calendar_date', 'activity_type_filter', 'entries_by_activity', 'goals'],
        preserveScroll: true,
      }
    )
  }

  const handleViewChange = (newView: string) => {
    const newViewType = newView as PeriodView
    setView(newViewType)
    if (newViewType === 'custom') {
      if (customRange?.from && customRange?.to) {
        fetchData(currentDate, newViewType, customRange, selectedActivityTypes)
      }
    } else {
      fetchData(currentDate, newViewType, undefined, selectedActivityTypes)
    }
  }

  const handleCustomRangeChange = (range: DateRange | undefined) => {
    setCustomRange(range)
    if (range?.from && range?.to) {
      fetchData(currentDate, 'custom', range, selectedActivityTypes)
    }
  }

  const handleActivityTypeFilterChange = (ids: number[]) => {
    setSelectedActivityTypes(ids)
    fetchData(currentDate, view, view === 'custom' ? customRange : undefined, ids)
  }

  const handleExport = () => {
    const params = new URLSearchParams()

    if (filters.start_date) {
      params.set('start_date', filters.start_date)
    }
    if (filters.end_date) {
      params.set('end_date', filters.end_date)
    }

    if (selectedActivityTypes.length > 0) {
      selectedActivityTypes.forEach(id => params.append('activity_type_ids[]', id.toString()))
    }

    window.location.href = `/time_entries/export?${params.toString()}`
  }

  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0]
  }

  const getTotalMinutes = (entries: TimeEntry[]) => {
    return entries.reduce((sum, e) => sum + (e.duration_minutes || 0), 0)
  }

  const isToday = (date: Date) => {
    const today = new Date()
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear()
  }

  const getNavigationLabel = () => {
    switch (view) {
      case 'day':
        return currentDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
      case 'week': {
        const dayOfWeek = currentDate.getDay()
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
        const weekStart = new Date(currentDate)
        weekStart.setDate(currentDate.getDate() + mondayOffset)
        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)
        return `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
      }
      case 'month':
        return currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
      case 'year':
        return currentDate.getFullYear().toString()
      case 'custom':
        return '' // Not used for custom view
    }
  }

  const getTodayLabel = () => {
    switch (view) {
      case 'day': return t('calendar.today')
      case 'week': return t('calendar.thisWeek')
      case 'month': return t('calendar.thisMonth')
      case 'year': return t('statistics.thisYear')
      default: return t('calendar.today')
    }
  }

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

  const renderDayView = () => {
    const dateKey = formatDateKey(currentDate)
    const dayEntries = entriesByDate[dateKey] || []

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">
          {currentDate.toLocaleDateString(undefined, { weekday: 'long' })}
        </h3>
        {dayEntries.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">{t('calendar.noEntriesForDay')}</p>
        ) : (
          <div className="space-y-2">
            {dayEntries.map((entry) => (
              <div
                key={entry.id}
                className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-accent"
                onClick={() => handleEntryClick(entry)}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="h-4 w-4 rounded-full"
                    style={{ backgroundColor: entry.display_color }}
                  />
                  <div>
                    <div className="font-medium">{entry.activity_name}</div>
                    {entry.notes && (
                      <div className="text-sm text-muted-foreground">{entry.notes}</div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="secondary">{entry.duration_display}</Badge>
                  {entry.started_at && entry.ended_at && (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {entry.started_at} - {entry.ended_at}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderWeekView = () => {
    const dayOfWeek = currentDate.getDay()
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
    const weekStart = new Date(currentDate)
    weekStart.setDate(currentDate.getDate() + mondayOffset)

    const weekDates: Date[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart)
      date.setDate(weekStart.getDate() + i)
      weekDates.push(date)
    }

    return (
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date, index) => {
          const dateKey = formatDateKey(date)
          const dayEntries = entriesByDate[dateKey] || []
          const totalMinutes = getTotalMinutes(dayEntries)

          return (
            <div
              key={index}
              className={`min-h-[200px] rounded-lg border p-2 ${isToday(date) ? 'border-primary bg-primary/5' : ''}`}
            >
              <div className="mb-2 text-center">
                <div className="text-xs text-muted-foreground">{weekDays[index]}</div>
                <div className={`text-lg font-medium ${isToday(date) ? 'text-primary' : ''}`}>
                  {date.getDate()}
                </div>
              </div>
              {dayEntries.length > 0 && (
                <div className="space-y-1">
                  {dayEntries.slice(0, 4).map((entry) => (
                    <div
                      key={entry.id}
                      className="flex cursor-pointer items-center gap-1 rounded p-1 text-xs hover:bg-accent"
                      title={entry.activity_name}
                      onClick={() => handleEntryClick(entry)}
                    >
                      <div
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: entry.display_color }}
                      />
                      <span className="truncate">{entry.activity_name}</span>
                    </div>
                  ))}
                  {dayEntries.length > 4 && (
                    <div className="text-xs text-muted-foreground">
                      +{dayEntries.length - 4} more
                    </div>
                  )}
                  <Badge variant="secondary" className="mt-1 text-xs">
                    {formatDuration(totalMinutes)}
                  </Badge>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderMonthView = () => {
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    let startDay = firstDayOfMonth.getDay() - 1
    if (startDay < 0) startDay = 6
    const daysInMonth = lastDayOfMonth.getDate()

    const calendarDays: (number | null)[] = []
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(null)
    }
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push(i)
    }

    return (
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day) => (
          <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
            {day}
          </div>
        ))}

        {calendarDays.map((day, index) => {
          if (day === null) {
            return <div key={`empty-${index}`} className="min-h-25 bg-muted/30" />
          }

          const date = new Date(year, month, day)
          const dateKey = formatDateKey(date)
          const dayEntries = entriesByDate[dateKey] || []
          const totalMinutes = getTotalMinutes(dayEntries)

          return (
            <div
              key={day}
              className={`min-h-25 border p-2 ${isToday(date) ? 'border-primary bg-primary/5' : ''}`}
            >
              <div className={`text-sm font-medium ${isToday(date) ? 'text-primary' : ''}`}>
                {day}
              </div>
              {dayEntries.length > 0 && (
                <div className="mt-1 space-y-1">
                  {dayEntries.slice(0, 3).map((entry) => (
                    <div
                      key={entry.id}
                      className="flex cursor-pointer items-center gap-1 text-xs hover:bg-accent"
                      title={entry.activity_name}
                      onClick={() => handleEntryClick(entry)}
                    >
                      <div
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: entry.display_color }}
                      />
                      <span className="truncate">{entry.activity_name}</span>
                    </div>
                  ))}
                  {dayEntries.length > 3 && (
                    <div className="text-xs text-muted-foreground">
                      +{dayEntries.length - 3} more
                    </div>
                  )}
                  <Badge variant="secondary" className="mt-1 text-xs">
                    {formatDuration(totalMinutes)}
                  </Badge>
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const renderYearView = () => {
    // Year view shows monthly summaries
    const months = []
    for (let m = 0; m < 12; m++) {
      const monthStart = new Date(year, m, 1)
      const monthEnd = new Date(year, m + 1, 0)

      let totalMinutes = 0
      let entryCount = 0

      for (let d = 1; d <= monthEnd.getDate(); d++) {
        const dateKey = formatDateKey(new Date(year, m, d))
        const dayEntries = entriesByDate[dateKey] || []
        totalMinutes += getTotalMinutes(dayEntries)
        entryCount += dayEntries.length
      }

      months.push({
        month: m,
        name: monthStart.toLocaleDateString(undefined, { month: 'short' }),
        totalMinutes,
        entryCount,
      })
    }

    return (
      <div className="grid grid-cols-4 gap-4">
        {months.map((m) => {
          const isCurrentMonth = new Date().getMonth() === m.month && new Date().getFullYear() === year

          return (
            <Card
              key={m.month}
              className={`cursor-pointer transition-colors hover:bg-accent ${isCurrentMonth ? 'border-primary' : ''}`}
              onClick={() => {
                setView('month')
                setCurrentDate(new Date(year, m.month, 1))
                fetchData(new Date(year, m.month, 1), 'month', undefined, selectedActivityTypes)
              }}
            >
              <CardContent className="p-4">
                <div className={`text-lg font-medium ${isCurrentMonth ? 'text-primary' : ''}`}>
                  {m.name}
                </div>
                {m.totalMinutes > 0 ? (
                  <div className="mt-2 text-sm text-muted-foreground">
                    {formatDuration(m.totalMinutes)} ({m.entryCount} entries)
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-muted-foreground">No entries</div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>
    )
  }

  const renderCustomView = () => {
    if (!customRange?.from || !customRange?.to) {
      return (
        <p className="py-8 text-center text-muted-foreground">
          {t('calendar.pickDateRange')}
        </p>
      )
    }

    const sortedEntries = [...time_entries].sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )

    if (sortedEntries.length === 0) {
      return (
        <p className="py-8 text-center text-muted-foreground">
          {t('calendar.noEntriesForPeriod')}
        </p>
      )
    }

    const groupedByDate: Record<string, TimeEntry[]> = {}
    for (const entry of sortedEntries) {
      if (!groupedByDate[entry.date]) {
        groupedByDate[entry.date] = []
      }
      groupedByDate[entry.date].push(entry)
    }

    return (
      <div className="space-y-6">
        {Object.entries(groupedByDate).map(([date, entries]) => {
          const dateObj = new Date(date)
          const totalMinutes = getTotalMinutes(entries)

          return (
            <div key={date} className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className={`text-sm font-medium ${isToday(dateObj) ? 'text-primary' : 'text-muted-foreground'}`}>
                  {isToday(dateObj) && <Badge variant="default" className="mr-2">{t('calendar.today')}</Badge>}
                  {dateObj.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <Badge variant="secondary">{formatDuration(totalMinutes)}</Badge>
              </div>
              <div className="space-y-2">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex cursor-pointer items-center justify-between rounded-lg border p-3 hover:bg-accent"
                    onClick={() => handleEntryClick(entry)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="h-4 w-4 rounded-full"
                        style={{ backgroundColor: entry.display_color }}
                      />
                      <div>
                        <div className="font-medium">{entry.activity_name}</div>
                        {entry.notes && (
                          <div className="text-sm text-muted-foreground">{entry.notes}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="secondary">{entry.duration_display}</Badge>
                      {entry.started_at && entry.ended_at && (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {entry.started_at} - {entry.ended_at}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Head title={t('timeEntries.title')} />

      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          <h1 className="text-2xl font-bold">{t('timeEntries.title')}</h1>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="container mx-auto space-y-6 px-4 py-6">
        {/* Period selector and navigation */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Tabs value={view} onValueChange={handleViewChange}>
              <TabsList>
                <TabsTrigger value="day">{t('calendar.day')}</TabsTrigger>
                <TabsTrigger value="week">{t('calendar.week')}</TabsTrigger>
                <TabsTrigger value="month">{t('calendar.month')}</TabsTrigger>
                <TabsTrigger value="year">{t('statistics.year')}</TabsTrigger>
                <TabsTrigger value="custom">{t('calendar.custom')}</TabsTrigger>
              </TabsList>
            </Tabs>

            <PeriodNavigator
              label={getNavigationLabel()}
              onPrevious={() => navigate('prev')}
              onNext={() => navigate('next')}
              onToday={goToToday}
              todayLabel={getTodayLabel()}
              isCustom={view === 'custom'}
              customRange={customRange}
              onCustomRangeChange={handleCustomRangeChange}
              customPlaceholder={t('calendar.pickDateRange')}
            />
          </div>

          <QuickAddModal
            activitySubtypes={activity_subtypes}
            activityTypes={activity_types}
            open={quickAddOpen}
            onOpenChange={setQuickAddOpen}
            trigger={
              <Button>
                <Plus className="h-4 w-4" />
                {t('timeEntries.addEntry')}
                <kbd className="ml-2 hidden rounded bg-primary-foreground/20 px-1.5 py-0.5 font-mono text-xs sm:inline-block">
                  N
                </kbd>
              </Button>
            }
          />
        </div>

        {/* Activity type filter and export */}
        <div className="flex items-center justify-between gap-4">
          <ActivityTypeFilter
            activityTypes={activity_types}
            selectedIds={selectedActivityTypes}
            onChange={handleActivityTypeFilterChange}
          />
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            {t('export.download')}
          </Button>
        </div>

        {/* Goal-Based Stacked Activity Bar */}
        {(activitiesWithGoals.length > 0 || activitiesWithoutGoals.length > 0) && (() => {
          // Calculate width for "Other" (activities without goals)
          const otherMinutes = activitiesWithoutGoals.reduce((sum, a) => sum + a.total_minutes, 0)
          // Use 80% of bar for goal activities, 20% for "other" if there are both
          const hasGoals = activitiesWithGoals.length > 0
          const hasOther = otherMinutes > 0
          const goalBarPercent = hasGoals && hasOther ? 80 : hasGoals ? 100 : 0
          const otherBarPercent = hasOther ? (hasGoals ? 20 : 100) : 0

          return (
            <div className="space-y-2">
              {/* Stacked bar */}
              <div className="flex items-center gap-3">
                <div className="relative h-6 flex-1 overflow-hidden bg-secondary">
                  <div className="absolute inset-0 flex">
                    {/* Goal-based segments */}
                    {activitiesWithGoals.map((activity) => {
                      const segmentWidth = totalTargetMinutes > 0
                        ? (activity.goal.target_minutes / totalTargetMinutes) * goalBarPercent
                        : 0
                      const fillPercent = Math.min(100, (activity.total_minutes / activity.goal.target_minutes) * 100)
                      const goalAchieved = activity.total_minutes >= activity.goal.target_minutes

                      return (
                        <div
                          key={activity.id}
                          className="relative h-full cursor-pointer overflow-hidden border-r border-background/20 last:border-r-0"
                          style={{ width: `${segmentWidth}%` }}
                          onClick={() => setStatsExpanded(!statsExpanded)}
                          title={`${activity.name}: ${formatDuration(activity.total_minutes)} / ${activity.goal.target_display}`}
                        >
                          {/* Filled portion (actual progress) */}
                          <div
                            className="absolute inset-y-0 left-0 transition-all"
                            style={{
                              width: `${fillPercent}%`,
                              backgroundColor: activity.color,
                            }}
                          />
                          {/* Empty portion (remaining to goal) */}
                          <div
                            className="absolute inset-y-0 right-0"
                            style={{
                              width: `${100 - fillPercent}%`,
                              backgroundColor: activity.color,
                              opacity: 0.2,
                            }}
                          />
                          {/* Show time in bar if segment is wide enough */}
                          {segmentWidth > 15 && (
                            <span className={`absolute inset-0 flex items-center justify-center gap-1 text-xs font-medium drop-shadow-sm ${fillPercent > 50 ? 'text-white' : 'text-foreground'}`}>
                              {goalAchieved && <Check className="h-3 w-3" />}
                              {formatDuration(activity.total_minutes)}/{activity.goal.target_display}
                            </span>
                          )}
                          {/* Show just check icon if segment is too small for text */}
                          {segmentWidth <= 15 && goalAchieved && (
                            <span className="absolute inset-0 flex items-center justify-center">
                              <Check className="h-3.5 w-3.5 text-white drop-shadow-sm" />
                            </span>
                          )}
                        </div>
                      )
                    })}
                    {/* Activities without goals - show each with their color */}
                    {activitiesWithoutGoals.map((activity) => {
                      const segmentWidth = hasGoals
                        ? (activity.total_minutes / otherMinutes) * otherBarPercent
                        : totalActualMinutes > 0
                          ? (activity.total_minutes / totalActualMinutes) * 100
                          : 0

                      return (
                        <div
                          key={activity.id}
                          className="relative h-full cursor-pointer border-r border-background/20 last:border-r-0"
                          style={{
                            width: `${segmentWidth}%`,
                            backgroundColor: activity.color,
                          }}
                          onClick={() => setStatsExpanded(!statsExpanded)}
                          title={`${activity.name}: ${formatDuration(activity.total_minutes)}`}
                        >
                          {segmentWidth > 12 && (
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white drop-shadow-sm">
                              {formatDuration(activity.total_minutes)}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
                <span className="shrink-0 text-sm font-medium">
                  {formatDuration(totalActualMinutes)}
                  {totalTargetMinutes > 0 && (
                    <span className="text-muted-foreground">/{formatDuration(totalTargetMinutes)}</span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 shrink-0 p-0"
                  onClick={() => setStatsExpanded(!statsExpanded)}
                >
                  {statsExpanded ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Legend */}
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {activitiesWithGoals.map((activity) => {
                  const goalAchieved = activity.total_minutes >= activity.goal.target_minutes

                  return (
                    <div
                      key={activity.id}
                      className="flex cursor-pointer items-center gap-1.5 text-sm"
                      onClick={() => setStatsExpanded(!statsExpanded)}
                    >
                      <div
                        className="h-2.5 w-2.5 rounded-sm"
                        style={{ backgroundColor: activity.color }}
                      />
                      <span className={goalAchieved ? 'text-green-600 dark:text-green-400' : ''}>
                        {activity.name}
                      </span>
                    </div>
                  )
                })}
                {activitiesWithoutGoals.map((activity) => (
                  <div
                    key={activity.id}
                    className="flex cursor-pointer items-center gap-1.5 text-sm"
                    onClick={() => setStatsExpanded(!statsExpanded)}
                  >
                    <div
                      className="h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: activity.color }}
                    />
                    <span>{activity.name}</span>
                  </div>
                ))}
              </div>

              {/* Expanded details */}
              {statsExpanded && (
                <div className="mt-3 space-y-2 rounded-lg border bg-muted/30 p-3">
                  {activitiesWithGoals.map((activity) => {
                    const fillPercent = Math.min(100, Math.round((activity.total_minutes / activity.goal.target_minutes) * 100))
                    const goalAchieved = activity.total_minutes >= activity.goal.target_minutes

                    // For type-level goals, check for subtypes
                    const isTypeGoal = !activity.isSubtype
                    const activityStats = isTypeGoal ? entries_by_activity.find(a => a.id === activity.id) : null
                    const allSubtypesForType = isTypeGoal ? activity_subtypes.filter(s => s.activity_type.id === activity.id) : []
                    const hasSubtypes = allSubtypesForType.length > 0
                    const isExpanded = expandedActivityTypes.has(activity.id)

                    return (
                      <Collapsible
                        key={activity.goal_key}
                        open={isExpanded}
                        onOpenChange={() => hasSubtypes && toggleActivityTypeExpanded(activity.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {hasSubtypes ? (
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                                  {isExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            ) : (
                              <div className="w-5" /> // Spacer for alignment
                            )}
                            <div
                              className="h-3 w-3 rounded"
                              style={{ backgroundColor: activity.color }}
                            />
                            <span className="text-sm font-medium">{activity.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${goalAchieved ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                              {formatDuration(activity.total_minutes)} / {activity.goal.target_display} ({fillPercent}%)
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (activity.isSubtype) {
                                  const subtype = activity_subtypes.find(s => s.id === activity.id)
                                  if (subtype) openGoalModal(null, subtype)
                                } else {
                                  openGoalModal({ id: activity.id, name: activity.name, color: activity.color, icon: '' })
                                }
                              }}
                            >
                              <Target className="h-3.5 w-3.5 text-primary" />
                            </Button>
                          </div>
                        </div>
                        {hasSubtypes && (
                          <CollapsibleContent>
                            <div className="ml-7 mt-1 space-y-1 border-l pl-3">
                              {allSubtypesForType.map((subtype) => {
                                // Look up time stats if available
                                const subtypeStats = activityStats?.subtypes.find(s => s.id === subtype.id)
                                const totalMinutes = subtypeStats?.total_minutes || 0
                                const hasGoal = subtypeIdsWithGoals.has(subtype.id)
                                const goal = goalsByKey[`subtype_${subtype.id}`]

                                return (
                                  <div key={subtype.id} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="h-2.5 w-2.5 rounded"
                                        style={{ backgroundColor: subtype.color }}
                                      />
                                      <span className="text-sm">{subtype.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasGoal && goal ? (
                                        <span className={`text-sm ${totalMinutes >= goal.target_minutes ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                                          {formatDuration(totalMinutes)} / {goal.target_display}
                                        </span>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">{formatDuration(totalMinutes)}</span>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-5 w-5 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          openGoalModal(null, subtype)
                                        }}
                                      >
                                        <Target className={`h-3 w-3 ${hasGoal ? 'text-primary' : 'text-muted-foreground'}`} />
                                      </Button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </CollapsibleContent>
                        )}
                      </Collapsible>
                    )
                  })}
                  {activityTypesWithoutGoals.map((activity) => {
                    // Get ALL subtypes for this activity type (not just ones with time logged)
                    const activityStats = entries_by_activity.find(a => a.id === activity.id)
                    const allSubtypes = activity_subtypes.filter(s => s.activity_type.id === activity.id)
                    const hasSubtypes = allSubtypes.length > 0
                    const isExpanded = expandedActivityTypes.has(activity.id)

                    return (
                      <Collapsible
                        key={activity.id}
                        open={isExpanded}
                        onOpenChange={() => hasSubtypes && toggleActivityTypeExpanded(activity.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {hasSubtypes ? (
                              <CollapsibleTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                                  {isExpanded ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                            ) : (
                              <div className="w-5" /> // Spacer for alignment
                            )}
                            <div
                              className="h-3 w-3 rounded"
                              style={{ backgroundColor: activity.color }}
                            />
                            <span className="text-sm font-medium">{activity.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">{formatDuration(activity.total_minutes)}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation()
                                openGoalModal({ id: activity.id, name: activity.name, color: activity.color, icon: activity.icon || '' })
                              }}
                            >
                              <Target className="h-3.5 w-3.5 text-muted-foreground" />
                            </Button>
                          </div>
                        </div>
                        {hasSubtypes && (
                          <CollapsibleContent>
                            <div className="ml-7 mt-1 space-y-1 border-l pl-3">
                              {allSubtypes.map((subtype) => {
                                // Look up time stats if available
                                const subtypeStats = activityStats?.subtypes.find(s => s.id === subtype.id)
                                const totalMinutes = subtypeStats?.total_minutes || 0
                                const hasGoal = subtypeIdsWithGoals.has(subtype.id)
                                const goal = goalsByKey[`subtype_${subtype.id}`]

                                return (
                                  <div key={subtype.id} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="h-2.5 w-2.5 rounded"
                                        style={{ backgroundColor: subtype.color }}
                                      />
                                      <span className="text-sm">{subtype.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {hasGoal && goal ? (
                                        <span className={`text-sm ${totalMinutes >= goal.target_minutes ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                                          {formatDuration(totalMinutes)} / {goal.target_display}
                                        </span>
                                      ) : (
                                        <span className="text-sm text-muted-foreground">{formatDuration(totalMinutes)}</span>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-5 w-5 p-0"
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          openGoalModal(null, subtype)
                                        }}
                                      >
                                        <Target className={`h-3 w-3 ${hasGoal ? 'text-primary' : 'text-muted-foreground'}`} />
                                      </Button>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </CollapsibleContent>
                        )}
                      </Collapsible>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}

        {/* Calendar View */}
        <Card className="py-4">
          <CardContent>
            {view === 'day' && renderDayView()}
            {view === 'week' && renderWeekView()}
            {view === 'month' && renderMonthView()}
            {view === 'year' && renderYearView()}
            {view === 'custom' && renderCustomView()}
          </CardContent>
        </Card>
      </main>

      <EditModal
        activitySubtypes={activity_subtypes}
        activityTypes={activity_types}
        timeEntry={editingEntry}
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
      />

      {(goalModalActivityType || goalModalActivitySubtype) && (
        <GoalModal
          {...(goalModalActivityType
            ? { activityType: goalModalActivityType }
            : { activitySubtype: goalModalActivitySubtype! }
          )}
          existingGoal={
            goalModalActivityType
              ? goalsByKey[`type_${goalModalActivityType.id}`]
              : goalsByKey[`subtype_${goalModalActivitySubtype!.id}`]
          }
          period={getGoalPeriod()}
          open={goalModalOpen}
          onOpenChange={setGoalModalOpen}
        />
      )}
    </div>
  )
}
